/**
 * Rooms and seasons — the two things the studio owns outright (D-157).
 *
 * Neither comes from Jackrabbit. The room colours the whole calendar is drawn
 * in, and the season the dates hang off, exist only because the baseline seeder
 * put them there — so until now a typo in either needed a developer.
 */

export type Room = {
  roomId: string
  name: string
  capacity: number | null
  color: string | null
  hasBarres: boolean
  hasMirrors: boolean
  hasSprungFloor: boolean
  notes: string | null
  /** Classes scheduled here. Nonzero means it cannot be retired yet. */
  classes: number
  isArchived: boolean
}

export type RoomEdit = {
  name: string
  capacity: number | null
  color: string | null
  hasBarres: boolean
  hasMirrors: boolean
  hasSprungFloor: boolean
  notes: string | null
}

export type Season = {
  seasonId: string
  name: string
  startDate: string
  endDate: string
  registrationOpensOn: string | null
  registrationClosesOn: string | null
  ageCutoffDate: string | null
  classes: number
  /** Today falls inside it — computed, never a stored flag that goes stale. */
  isCurrent: boolean
}

export type SeasonEdit = {
  name: string
  startDate: string
  endDate: string
  registrationOpensOn: string | null
  registrationClosesOn: string | null
  ageCutoffDate: string | null
}

/**
 * A date the studio cares about, in Tonya's words (D-555).
 *
 * A marker and nothing more: it closes nothing and cancels no class. Reading
 * the list needs only `ViewSchedule`, because a task's deadline can be hung on
 * one and every member of staff may raise a task; changing it is the studio's.
 */
export type StudioDate = {
  studioDateId: string
  name: string
  on: string
  note: string | null
  /** Already gone by — computed against the studio's today, never stored. */
  isPast: boolean
  isArchived: boolean
}

export type StudioDateEdit = {
  name: string
  on: string
  note: string | null
}

async function send<T>(url: string, method: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.detail ?? problem?.title ?? `That did not work (${res.status})`)
  }

  return res.json()
}

export const fetchRooms = () => send<Room[]>('/api/admin/rooms/', 'GET')
export const createRoom = (edit: RoomEdit) => send<Room[]>('/api/admin/rooms/', 'POST', edit)
export const saveRoom = (id: string, edit: RoomEdit) =>
  send<Room[]>(`/api/admin/rooms/${id}`, 'PUT', edit)
export const archiveRoom = (id: string) => send<Room[]>(`/api/admin/rooms/${id}`, 'DELETE')

export const fetchSeasons = () => send<Season[]>('/api/admin/seasons/', 'GET')
export const createSeason = (edit: SeasonEdit) => send<Season[]>('/api/admin/seasons/', 'POST', edit)
export const saveSeason = (id: string, edit: SeasonEdit) =>
  send<Season[]>(`/api/admin/seasons/${id}`, 'PUT', edit)

export const fetchStudioDates = () => send<StudioDate[]>('/api/admin/studio-dates/', 'GET')
export const createStudioDate = (edit: StudioDateEdit) =>
  send<StudioDate[]>('/api/admin/studio-dates/', 'POST', edit)
export const saveStudioDate = (id: string, edit: StudioDateEdit) =>
  send<StudioDate[]>(`/api/admin/studio-dates/${id}`, 'PUT', edit)
export const archiveStudioDate = (id: string) =>
  send<StudioDate[]>(`/api/admin/studio-dates/${id}`, 'DELETE')

/**
 * What the picker may offer: still to come, not retired, soonest first.
 *
 * The screen keeps showing past dates under their own heading — a mistyped
 * year has to read as a wrong date rather than as a row that quietly never
 * appears — so the filtering happens here rather than in the query.
 */
export function upcoming(dates: StudioDate[]): StudioDate[] {
  return dates.filter((d) => !d.isPast && !d.isArchived).sort((a, b) => a.on.localeCompare(b.on))
}

/**
 * Whether a studio date can be saved, as a sentence or null.
 *
 * A past date is allowed on purpose: last year's Regionals is a fact, and
 * refusing it would make the list unable to describe the season it is in.
 *
 * The lengths match the columns. Without them the database refuses instead,
 * and a refusal from the database can only be guessed at — a 130-character
 * name came back as "that one is already there", about a row that did not
 * exist.
 */
export function studioDateProblem(edit: StudioDateEdit): string | null {
  if (!edit.name.trim()) return 'Give it a name.'
  if (edit.name.trim().length > 120) return 'That name is too long.'
  if (!edit.on) return 'Give it a date.'
  if ((edit.note ?? '').trim().length > 500) return 'That note is too long.'

  return null
}

/**
 * What a room offers, as a short readable list.
 *
 * Only what it has. "No barres, no mirrors, sprung floor" is three facts where
 * one was wanted, and reads as a complaint about the room.
 */
export function capabilityLabel(room: Room): string {
  const has = [
    room.hasBarres && 'barres',
    room.hasMirrors && 'mirrors',
    room.hasSprungFloor && 'sprung floor',
  ].filter((x): x is string => Boolean(x))

  return has.join(' · ')
}

/**
 * Why a room cannot be retired yet, or null when it can.
 *
 * Returned as a sentence rather than a boolean, because "you cannot" without
 * "because eleven classes are in it" leaves somebody clicking a dead button.
 */
export function retireBlocker(room: Room): string | null {
  if (room.isArchived) return 'Already retired.'
  if (room.classes === 0) return null

  return room.classes === 1
    ? '1 class is still scheduled here.'
    : `${room.classes} classes are still scheduled here.`
}

/**
 * Whether a season's dates make sense, as a message or null.
 *
 * Checked here as well as on the server so the answer arrives while somebody is
 * still looking at the field they got wrong.
 */
export function seasonDateProblem(edit: SeasonEdit): string | null {
  if (!edit.startDate || !edit.endDate) return 'Both dates are needed.'
  if (edit.endDate < edit.startDate) return 'The season cannot end before it starts.'

  const { registrationOpensOn: opens, registrationClosesOn: closes } = edit
  if (opens && closes && closes < opens) return 'Registration closes before it opens.'

  return null
}
